/**
 * Filters for VSC reports.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { SearchInput } from '@/components/forms/SearchInput';
import {
  DATE_RANGE_PERIOD_OPTIONS_FULL,
  VSC_REPORT_STATUS_CONFIG,
  VSC_REPORT_STATUS_FILTER_KEYS,
} from './vsc-report-configs';
import { formatVscFilterDateToString, parseVscFilterDateInput } from './vsc-report-table-utils';
import type { VscReportFilters } from '@/types/vsc-report.types';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';

/** Props for {@link VscReportFiltersContent}. */
interface VscReportFiltersContentProps {
  filters: VscReportFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onSetDateRange: (range: VscReportFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetEstablishment?: (id: string | undefined) => void;
  onSetSearch?: (value: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Period chips; picking one clears the custom date range so the two stay exclusive. */
const PeriodFilter: React.FC<{
  filters: VscReportFilters;
  onSetDateRange: (range: VscReportFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Période</span>
      <SegmentedControl
        size="sm"
        value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
        onChange={(v) => {
          onSetDateRange(v as VscReportFilters['dateRange']);
          onSetCustomDateRange(undefined, undefined);
        }}
        options={[...DATE_RANGE_PERIOD_OPTIONS_FULL]}
      />
    </div>
  );
};

/** Custom from/to dates; setting either forces `dateRange` back to `all`. */
const DateRangeFilter: React.FC<{
  filters: VscReportFilters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetDateRange: (range: VscReportFilters['dateRange']) => void;
}> = ({ filters, onSetCustomDateRange, onSetDateRange }) => {
  const fromStr = formatVscFilterDateToString(filters.customDateFrom);
  const toStr = formatVscFilterDateToString(filters.customDateTo);

  /** Empty value clears the start; a date starts at 00:00 local. */
  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    onSetCustomDateRange(parseVscFilterDateInput(val, false), filters.customDateTo);
    onSetDateRange('all');
  };

  /** Empty value clears the end; a date ends at 23:59:59 local. */
  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    onSetCustomDateRange(filters.customDateFrom, parseVscFilterDateInput(val, true));
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date personnalisée</span>
      <div className="w-auto mt-0.5">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Multi-select status chips (`to_fill` / `submitted` / `validated`). */
const StatusFilter: React.FC<{
  filters: VscReportFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">
        {VSC_REPORT_STATUS_FILTER_KEYS.map((key) => {
          const config = VSC_REPORT_STATUS_CONFIG[key];
          const Icon = config.icon;
          const isActive = filters.statuses.includes(key);
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={
                {
                  '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
                } as React.CSSProperties
              }
              onClick={() => onToggleStatus(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Single-establishment autocomplete stored as `establishmentId`. */
const EstablishmentFilter: React.FC<{
  filters: VscReportFilters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={options}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Debounced volunteer name search; empty string becomes `undefined`. */
const SearchFilter: React.FC<{
  filters: VscReportFilters;
  onSetSearch: (value: string | undefined) => void;
}> = ({ filters, onSetSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Nom, prénom du VSC..."
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Filters for VSC reports. */
export const VscReportFiltersContent: React.FC<VscReportFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onSetDateRange,
  onSetCustomDateRange,
  onSetEstablishment,
  onSetSearch,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-search') && onSetSearch && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} />
      )}
      {show('filter-period') && (
        <PeriodFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}
      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}
      {show('filter-date-range') && (
        <DateRangeFilter
          filters={filters}
          onSetCustomDateRange={onSetCustomDateRange}
          onSetDateRange={onSetDateRange}
        />
      )}
      {show('filter-establishment') && onSetEstablishment && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}
    </div>
  );
};
